<script setup lang="ts">
import type { UserFavorites } from '~/composables/useFavorites'
import type { JournalArticle } from '~/utils/solr/journal'
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import type { BookAndArticle } from '~/utils/solr/literature'
import { faBookOpen, faBooks, faCircleInfo } from '@fortawesome/pro-light-svg-icons'
import type { RouteLocationRaw } from '#vue-router'

type Content = BookAndArticle | LawAndCommentary | JournalArticle

type UserFavoritesKey = keyof UserFavorites

const props = defineProps<{
  content: Content | null
  loading?: boolean
  hideDownloadButton?: boolean
  hideAboutBookButton?: boolean
  showSourcesButton?: boolean
  disableDownloadLabel?: string
  enableDarkMode?: boolean
  journalIssuesLink?: RouteLocationRaw
}>()

const emit = defineEmits<{
  (e: 'openDownload'): void
  (e: 'openSources'): void
}>()

const hasLicense = useHasLicense()

const darkMode = defineModel<boolean>('darkMode', { default: false })

const { isFavorite, toggleFavorite } = useFavorites()

const isLaw = computed(() => props.content && 'source' in props.content)
const isBook = computed(() => props.content?.nodeType === 'book')
const isAcademicArticle = computed(() => props.content?.nodeType === 'academic_article')
const isJournal = computed(() => props.content?.nodeType === 'journal_article' || props.content?.nodeType === 'journal_editorial')

const favoriteCategory = computed<UserFavoritesKey | undefined>(() => {
  if (isLaw.value) {
    return 'law'
  }
  if (isBook.value || isAcademicArticle.value) {
    return 'literature'
  }
  if (isJournal.value) {
    return 'journals'
  }
  return undefined
})
</script>

<template>
  <section v-if="loading" class="flex gap-xs w-full justify-end">
    <Skeleton class="h-8 w-32 rounded-lg" />
  </section>
  <section v-if="content" class="flex flex-1 justify-end gap-xs">
    <ReadingDarkModeToggle v-if="enableDarkMode && hasLicense" v-model="darkMode" />
    <!-- TODO: link to "til utgaver" should take you to the correct place -->
    <NuxtLink
      v-if="journalIssuesLink"
      class="no-underline !w-fit p-xs lg:px-s lg:py-2.5xs flex gap-xs [&>svg]:!size-5"
      aria-label="Til utgaver"
      :class="buttonVariants.favorite.base({ intent: 'filled', layout: 'reading' })"
      :to="journalIssuesLink"
    >
      <Icon :icon="faBookOpen" /><span class="font-medium text-sm hidden nav:block">Til&nbsp;utgaver</span>
    </NuxtLink>
    <NuxtLink
      v-if="!hideAboutBookButton && isBook && content.id && content.nodeType !== 'academic_article'"
      class="no-underline !w-fit p-xs lg:px-s lg:py-2.5xs flex gap-xs [&>svg]:!size-5"
      aria-label="Om boken"
      :class="buttonVariants.favorite.base({ intent: 'filled', layout: 'reading' })"
      :to="{ name: 'book-about-by-id', params: { bokId: content.id } }"
    >
      <Icon :icon="faCircleInfo" /><span class="font-medium text-sm hidden nav:block">Om boken</span>
    </NuxtLink>
    <NuxtLink
      v-else-if="!hideAboutBookButton && isLaw"
      :to="{ hash: '#om-boken', query: $route.query }"
      class="no-underline !w-fit p-xs lg:px-s lg:py-2.5xs flex gap-xs [&>svg]:!size-5"
      aria-label="Om kommentaren"
      :class="buttonVariants.favorite.base({ intent: 'filled', layout: 'reading' })"
    >
      <Icon :icon="faCircleInfo" />
      <span class="font-medium text-sm hidden nav:block">Om kommentaren</span>
    </NuxtLink>
    <button
      v-if="showSourcesButton"
      class="no-underline !w-fit"
      :class="buttonVariants.favorite.base({ intent: 'filled', layout: 'reading' })"
      aria-label="Åpne litteraturliste"
      @click="emit('openSources')"
    >
      <Icon :icon="faBooks" />
      <span class="font-medium text-sm hidden nav:block">Litteraturliste</span>
    </button>
    <Tooltip v-if="!hideDownloadButton && disableDownloadLabel" :label="disableDownloadLabel" as-child>
      <template #trigger>
        <ReadingDownloadButton disabled layout="reading" @click="emit('openDownload')" />
      </template>
    </Tooltip>
    <ReadingDownloadButton v-else-if="!hideDownloadButton" layout="reading" @click="emit('openDownload')" />
    <FavoriteButton
      v-if="favoriteCategory"
      layout="reading"
      :selected="isFavorite(favoriteCategory, content.id)"
      :aria-label="isFavorite(favoriteCategory, content.id) ? `Fjern ${content?.title} fra favoritter` : `Legg til ${content?.title} i favoritter`"
      @click.prevent="toggleFavorite(favoriteCategory, content.id)"
    >
      <template #label>
        <span class="hidden lg:block">{{ isFavorite(favoriteCategory, content.id) ? 'Fjern fra favoritter' : 'Lagre som favoritt' }}</span>
      </template>
    </FavoriteButton>
  </section>
</template>
